/**
 * Runs async jobs one at a time. While a job is in flight, further
 * schedule() calls keep only the latest pending job (previous pending
 * is dropped). Prevents parallel XHRs when Redux fires rapid saves.
 */
export function createLatestWinsAsyncQueue() {
  let inFlight = false;
  let pending = null;

  const settleAndContinue = (settle, value) => {
    settle(value);
    if (pending) {
      const next = pending;
      pending = null;
      Promise.resolve()
        .then(() => next.job())
        .then(
          result => settleAndContinue(next.resolve, result),
          err => settleAndContinue(next.reject, err)
        );
    } else {
      inFlight = false;
    }
  };

  return {
    /**
     * @param {() => any | Promise<any>} job
     * @returns {Promise<any>}
     */
    schedule(job) {
      return new Promise((resolve, reject) => {
        if (inFlight) {
          if (pending) {
            pending.resolve(undefined);
          }
          pending = { job, resolve, reject };
          return;
        }

        inFlight = true;
        Promise.resolve()
          .then(() => job())
          .then(
            result => settleAndContinue(resolve, result),
            err => settleAndContinue(reject, err)
          );
      });
    },

    get isBusy() {
      return inFlight;
    },

    /** Test / session-reset helper */
    reset() {
      inFlight = false;
      pending = null;
    }
  };
}
